import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { CHAT_ONBOARDING_STEPS } from './steps';

const ChatOnboardingContext = createContext(null);

export const useChatOnboardingContext = () => {
  const context = useContext(ChatOnboardingContext);
  if (!context) {
    throw new Error('useChatOnboardingContext must be used within ChatOnboardingController');
  }
  return context;
};

const ChatOnboardingController = ({ children, onComplete, onSkip, userId }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [progress, setProgress] = useState(0);

  const getStorageKey = useCallback(() => {
    return userId ? `pickaiChatOnboarding_${userId}` : 'pickaiChatOnboarding_anonymous';
  }, [userId]);

  useEffect(() => {
    try {
      const storageKey = getStorageKey();
      const completed = localStorage.getItem(`${storageKey}_completed`);
      const version = localStorage.getItem(`${storageKey}_version`);
      const currentVersion = '1.0.0';

      if (completed === 'true' && version === currentVersion) {
        setHasCompleted(true);
      }
    } catch (error) {
      // Handle localStorage access errors (e.g., private browsing mode)
      console.warn('Chat Onboarding: Could not access localStorage', error);
      setHasCompleted(false);
    }
  }, [getStorageKey]);

  useEffect(() => {
    if (currentStep >= 0 && CHAT_ONBOARDING_STEPS.length > 0) {
      setProgress(((currentStep + 1) / CHAT_ONBOARDING_STEPS.length) * 100);
    }
  }, [currentStep]);

  const startOnboarding = useCallback(() => {
    try {
      // Always allow starting the tour when manually triggered
      setIsActive(true);
      setCurrentStep(0);
      document.body.style.overflow = 'hidden';
    } catch (error) {
      console.error('[Chat Onboarding] Error starting tour:', error);
      // Reset state on error
      setIsActive(false);
      document.body.style.overflow = '';
    }
  }, []);

  const skipOnboarding = useCallback(() => {
    const storageKey = getStorageKey();
    setIsActive(false);
    setCurrentStep(0);
    document.body.style.overflow = 'unset';
    try {
      localStorage.setItem(`${storageKey}_skipped`, 'true');
    } catch (error) {
      console.warn('Chat Onboarding: Could not save skip state', error);
    }
    onSkip?.();
  }, [onSkip, getStorageKey]);

  const completeOnboarding = useCallback(() => {
    const storageKey = getStorageKey();
    setIsActive(false);
    setHasCompleted(true);
    document.body.style.overflow = 'unset';
    try {
      localStorage.setItem(`${storageKey}_completed`, 'true');
      localStorage.setItem(`${storageKey}_version`, '1.0.0');
    } catch (error) {
      console.warn('Chat Onboarding: Could not save completion state', error);
    }
    onComplete?.();
  }, [onComplete, getStorageKey]);

  const nextStep = useCallback(() => {
    if (currentStep < CHAT_ONBOARDING_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  }, [currentStep, completeOnboarding]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < CHAT_ONBOARDING_STEPS.length) {
      setCurrentStep(stepIndex);
    }
  }, []);

  const resetOnboarding = useCallback(() => {
    const storageKey = getStorageKey();
    localStorage.removeItem(`${storageKey}_completed`);
    localStorage.removeItem(`${storageKey}_skipped`);
    localStorage.removeItem(`${storageKey}_version`);
    setHasCompleted(false);
    setCurrentStep(0);
    setIsActive(false);
  }, [getStorageKey]);

  const value = {
    isActive,
    currentStep,
    hasCompleted,
    progress,
    totalSteps: CHAT_ONBOARDING_STEPS.length,
    currentStepData: CHAT_ONBOARDING_STEPS[currentStep],
    startOnboarding,
    skipOnboarding,
    completeOnboarding,
    nextStep,
    previousStep,
    goToStep,
    resetOnboarding,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === CHAT_ONBOARDING_STEPS.length - 1,
  };

  return (
    <ChatOnboardingContext.Provider value={value}>
      {children}
    </ChatOnboardingContext.Provider>
  );
};

export default ChatOnboardingController;